import { useEffect, useRef, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useAvailabilityGuard } from '@/hooks/useAvailabilityGuard'
import type { InsInputTelElement } from '@/types/insites-components'
import {
  createContact,
  updateContact,
  validateEmail,
  saveCheckoutSession,
  syncCheckoutSession,
  type ValidateEmailResponse,
} from '@/lib/ecommerce/api'
import {
  checkoutStorage,
  consumeInternalCheckoutBack,
  isBackForwardNavigation,
  isInternalCheckoutBack,
} from '@/lib/ecommerce/checkoutStorage'
import {
  CheckoutPageShell,
  CheckoutFieldRow,
  CHECKOUT_INPUT_CLASS,
  CHECKOUT_SUBMIT_CLASS,
} from '@/components/ecommerce/CheckoutSteps'
import FlipButton from '@/components/ui/FlipButton'

/*
  Checkout Step 1 — Order contact (Teamwork 26100595).
  See in-file comment in the previous revision for the full anonymous /
  authenticated branch logic. Styling lives in
  src/styles/components/checkout-steps.css.
*/

interface CheckoutContactPayload {
  is_authenticated: boolean
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_country_code: string
  company_name: string
  contact_uuid: string
  session_id: string | null
}

interface DetailsForm {
  first_name: string
  last_name: string
  email: string
  phone: string
  phone_country_code: string
  company_name: string
}

type FieldErrors = Partial<Record<keyof DetailsForm, string>>

function toBareDialCode(value: string | null | undefined): string {
  if (!value) return '61'
  const trimmed = value.trim().replace(/^\+/, '')
  return trimmed || '61'
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* Key for the per-keystroke draft kept in sessionStorage. Matches the
   pattern used by CheckoutShipping / CheckoutBilling — the goal is
   that pressing the browser Back button from step 2 / 3 / 4 to
   /checkout restores everything the buyer had typed, while a fresh
   navigation in (Cart → Checkout link) or a manual refresh starts
   from a clean slate. The decision is made by inspecting the
   PerformanceNavigation type at mount time (see
   `isBackForwardNavigation` below). */
const DRAFT_KEY = 'mig-checkout-contact-draft'

function validate(state: DetailsForm): FieldErrors {
  const errors: FieldErrors = {}
  if (!state.first_name.trim()) errors.first_name = 'First name is required.'
  if (!state.last_name.trim()) errors.last_name = 'Last name is required.'
  if (!state.email.trim()) {
    errors.email = 'Email is required.'
  } else if (!EMAIL_RE.test(state.email.trim())) {
    errors.email = 'Please enter a valid email.'
  }
  if (!state.phone.trim()) errors.phone = 'Phone number is required.'
  return errors
}

export default function CheckoutContact() {
  useAvailabilityGuard()
  const payload = usePageData<CheckoutContactPayload>() ?? {
    is_authenticated: false,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_country_code: '+61',
    company_name: '',
    contact_uuid: '',
    session_id: null,
  }

  const [state, setState] = useState<DetailsForm>(() => {
    const initialFromPayload: DetailsForm = {
      first_name: payload.first_name ?? '',
      last_name: payload.last_name ?? '',
      email: payload.email ?? '',
      phone: payload.phone ?? '',
      phone_country_code: toBareDialCode(payload.phone_country_code),
      company_name: payload.company_name ?? '',
    }
    if (typeof window === 'undefined') return initialFromPayload

    // Rehydrate the draft when either:
    //   (a) the browser navigated here via back/forward — buyer is
    //       returning from step 2/3/4 via the browser Back button;
    //   (b) the buyer just clicked the in-page BACK link on the next
    //       step, which sets `mig-checkout-internal-back` in
    //       sessionStorage before letting the anchor navigate. The
    //       flag is consumed here so it's strictly one-shot.
    // Any other entry path (clicking Checkout from /cart → fresh
    // navigate; pressing Cmd-R / F5 → reload; opening /checkout in a
    // brand-new tab) intentionally starts with empty inputs and wipes
    // any stale draft.
    const cameFromInPageBack = isInternalCheckoutBack()
    if (cameFromInPageBack) consumeInternalCheckoutBack()
    if (!isBackForwardNavigation() && !cameFromInPageBack) {
      try {
        window.sessionStorage.removeItem(DRAFT_KEY)
      } catch {
        // sessionStorage may throw in private windows / over quota —
        // silent fallback to initialFromPayload is fine.
      }
      return initialFromPayload
    }

    try {
      const raw = window.sessionStorage.getItem(DRAFT_KEY)
      if (!raw) return initialFromPayload
      const parsed = JSON.parse(raw)
      return { ...initialFromPayload, ...parsed }
    } catch {
      return initialFromPayload
    }
  })

  // Persist the in-progress form to sessionStorage on every change so
  // a back-nav from a later step can restore it. The matching read is
  // in the useState initializer above (gated on back/forward nav).
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state))
    } catch {
      // sessionStorage can throw in private windows / quota — silent.
    }
  }, [state])

  const telRef = useRef<InsInputTelElement | null>(null)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<{
    kind: 'plain' | 'sign-in'
    message: string
    email?: string
  } | null>(null)

  function update<K extends keyof DetailsForm>(key: K, value: DetailsForm[K]) {
    setState((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
    if (submitError) setSubmitError(null)
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    let phoneFromTel = state.phone
    let codeFromTel = state.phone_country_code
    try {
      const values = await telRef.current?.getValues()
      if (values) {
        phoneFromTel = values.phone_number ?? state.phone
        codeFromTel = values.country_code ?? state.phone_country_code
      }
    } catch {
      // Fall back to the React state values if getValues throws.
    }

    const submittingState: DetailsForm = {
      ...state,
      phone: phoneFromTel,
      phone_country_code: codeFromTel,
    }

    const fieldErrors = validate(submittingState)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) {
      setSubmitError(null)
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    const trimmed = {
      first_name: submittingState.first_name.trim(),
      last_name: submittingState.last_name.trim(),
      email: submittingState.email.trim(),
      phone: submittingState.phone.trim(),
      phone_country_code: submittingState.phone_country_code,
      company_name: submittingState.company_name.trim(),
    }

    try {
      let resolvedContactUuid: string | null = null

      if (payload.is_authenticated && payload.contact_uuid) {
        await updateContact({
          uuid: payload.contact_uuid,
          first_name: trimmed.first_name,
          last_name: trimmed.last_name,
          email: trimmed.email,
          mobile_phone_number: trimmed.phone,
          mobile_phone_country_code: trimmed.phone_country_code,
          company_name: trimmed.company_name,
        })
        resolvedContactUuid = payload.contact_uuid
      } else {
        const lookup: ValidateEmailResponse = await validateEmail(trimmed.email)
        const match = lookup.items?.results?.[0]
        const hasProfile = !!match && Array.isArray(match.profiles) && match.profiles.length > 0

        if (match && hasProfile) {
          setSubmitError({
            kind: 'sign-in',
            email: trimmed.email,
            message: 'An account already exists with this email.',
          })
          setSubmitting(false)
          return
        }

        if (match) {
          await updateContact({
            uuid: match.external_id,
            first_name: trimmed.first_name,
            last_name: trimmed.last_name,
            email: trimmed.email,
            mobile_phone_number: trimmed.phone,
            mobile_phone_country_code: trimmed.phone_country_code,
            company_name: trimmed.company_name,
          })
          resolvedContactUuid = match.external_id
        } else {
          const created = await createContact({
            first_name: trimmed.first_name,
            last_name: trimmed.last_name,
            email: trimmed.email,
            mobile_phone_number: trimmed.phone,
            mobile_phone_country_code: trimmed.phone_country_code,
            company_name: trimmed.company_name,
          })
          resolvedContactUuid = created?.uuid ?? null
        }
      }

      await saveCheckoutSession({
        type: 'contact',
        latest_step: 2,
        guest_uuid: resolvedContactUuid ?? undefined,
        contact_email: trimmed.email,
        contact_first_name: trimmed.first_name,
        contact_last_name: trimmed.last_name,
        contact_phone_number: trimmed.phone,
        contact_phone_country_code: trimmed.phone_country_code,
        contact_company_name: trimmed.company_name,
      })
      await syncCheckoutSession({
        contact_email: trimmed.email,
        contact_first_name: trimmed.first_name,
        contact_last_name: trimmed.last_name,
        contact_phone_number: trimmed.phone,
        contact_phone_country_code: trimmed.phone_country_code,
        contact_company_name: trimmed.company_name,
        guest_uuid: resolvedContactUuid ?? undefined,
      })
      checkoutStorage.saveContact({
        contact_company_name: trimmed.company_name,
        contact_first_name: trimmed.first_name,
        contact_last_name: trimmed.last_name,
        contact_email: trimmed.email,
        contact_phone_number: trimmed.phone,
        contact_phone_country_code: trimmed.phone_country_code,
        resolved_contact_uuid: resolvedContactUuid,
      })

      // Overwrite the back-nav draft with the resolved values that
      // were actually submitted, NOT the pre-submit React state. The
      // useEffect-driven draft persistence only sees `state.phone`,
      // and the <ins-input-tel> web component doesn't push its
      // typed value back into React state on every keystroke — the
      // real phone surfaces only here, via telRef.current.getValues().
      // Without this write the draft would have `phone: ''` and a
      // browser back-nav from step 2 / 3 / 4 to /checkout would
      // restore every other field but leave the phone widget empty.
      // Done synchronously before navigation so the next page's
      // mount sees a consistent draft.
      try {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(submittingState))
      } catch {
        // sessionStorage may throw in private windows / quota — silent.
      }
      window.location.href = '/checkout/shipping'
    } catch (err) {
      setSubmitError({
        kind: 'plain',
        message: err instanceof Error ? err.message : 'Could not save contact details.',
      })
      setSubmitting(false)
    }
  }

  return (
    <CheckoutPageShell title="Checkout" active="contact" backHref="/cart">
      <form onSubmit={handleSubmit} noValidate className="mig-form mig-checkout-form">
        <CheckoutFieldRow label="Company Name (optional)">
          <input
            type="text"
            autoComplete="organization"
            placeholder="Enter company name"
            value={state.company_name}
            onChange={(e) => update('company_name', e.target.value)}
            disabled={submitting}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>

        <CheckoutFieldRow label="First Name" required>
          <input
            type="text"
            autoComplete="given-name"
            placeholder="Enter first name"
            aria-invalid={!!errors.first_name}
            aria-describedby={errors.first_name ? 'mig-co-fn-err' : undefined}
            value={state.first_name}
            onChange={(e) => update('first_name', e.target.value)}
            disabled={submitting}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.first_name && <FieldError id="mig-co-fn-err">{errors.first_name}</FieldError>}

        <CheckoutFieldRow label="Last Name" required>
          <input
            type="text"
            autoComplete="family-name"
            placeholder="Enter last name"
            aria-invalid={!!errors.last_name}
            aria-describedby={errors.last_name ? 'mig-co-ln-err' : undefined}
            value={state.last_name}
            onChange={(e) => update('last_name', e.target.value)}
            disabled={submitting}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.last_name && <FieldError id="mig-co-ln-err">{errors.last_name}</FieldError>}

        <CheckoutFieldRow label="Email" required>
          <input
            type="email"
            autoComplete="email"
            placeholder="Enter email address"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'mig-co-em-err' : undefined}
            value={state.email}
            onChange={(e) => update('email', e.target.value)}
            disabled={submitting || payload.is_authenticated}
            className={CHECKOUT_INPUT_CLASS}
          />
        </CheckoutFieldRow>
        {errors.email && <FieldError id="mig-co-em-err">{errors.email}</FieldError>}

        <CheckoutFieldRow label="Phone" required>
          <ins-input-tel
            ref={telRef as React.Ref<InsInputTelElement>}
            country-code={state.phone_country_code}
            phonenum-value={state.phone}
            phonenum-placeholder="Enter phone number"
            no-areacode
            aria-describedby={errors.phone ? 'mig-co-ph-err' : undefined}
          />
        </CheckoutFieldRow>
        {errors.phone && <FieldError id="mig-co-ph-err">{errors.phone}</FieldError>}

        {submitError && (
          <p role="alert" className="mig-checkout-submit-error">
            {submitError.message}
            {submitError.kind === 'sign-in' && (
              <>
                {' '}
                <a
                  href={`/sign-in?return_to=${encodeURIComponent('/checkout')}${
                    submitError.email ? `&email=${encodeURIComponent(submitError.email)}` : ''
                  }`}
                >
                  Sign in to continue
                </a>
                .
              </>
            )}
          </p>
        )}

        <div className="mig-checkout-submit-row">
          <FlipButton
            type="submit"
            disabled={submitting}
            className={CHECKOUT_SUBMIT_CLASS}
            data-pending={submitting}
          >
            {submitting ? 'Saving…' : 'Continue to Shipping'}
          </FlipButton>
        </div>
      </form>
    </CheckoutPageShell>
  )
}

function FieldError({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p id={id} role="alert" className="mig-checkout-field-error">
      {children}
    </p>
  )
}
